import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { fireErrorAnalytics, fireSiteOptedOutAnalytics } from '../../services/analytics/index.tsx';
import { getOrUpdateFeatureVisited } from '../../services/caching/index.tsx';
import { checkJpdCrossJoinNudgeEligibility } from '../../services/jira-jpd/eligibility.tsx';
import type { EligibilityCheck } from './types.tsx';

export const useJpdCrossJoinNudgeEligibility = () => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const licensedProducts = useLicensedProducts();
	const isJpdRoute = useIsActiveRoute('polaris-ideas');

	return useCallback(async (): Promise<EligibilityCheck> => {
		try {
			if (getOrUpdateFeatureVisited('crossJoinNudgesJiraToJpd', 30)) {
				return { isEligible: false, inelligibleReason: 'feature visited too recently' };
			}

			if (isJpdRoute) {
				return { isEligible: false, inelligibleReason: 'jpd route' };
			}

			const eligibilityResponse = await checkJpdCrossJoinNudgeEligibility({
				cloudId,
				userId,
				licensedProducts,
			});
			if (!eligibilityResponse.eligible) {
				if (eligibilityResponse.ineligibilityReason === 'SITE_OPTED_OUT') {
					fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesJiraToJpd');
				}
				return { isEligible: false, inelligibleReason: eligibilityResponse.ineligibilityReason };
			}
			return { isEligible: true, data: {} };

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('crossJoinNudgesJiraToJpd', error, { cloudId });
			return { isEligible: false, inelligibleReason: 'unexpected error' };
		}
	}, [cloudId, userId, createAnalyticsEvent, isJpdRoute, licensedProducts]);
};
